import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2, Loader2, MessageSquare } from "lucide-react";

const CONTACT_INFO = [
  { icon: Mail, title: "Email", content: "gabiwagnew@gmail.com", link: "mailto:gabiwagnew@gmail.com" },
  { icon: Phone, title: "Phone", content: "+251 914 080 045", link: "tel:+251914080045" },
  { icon: MapPin, title: "Location", content: "Addis Ababa, Ethiopia", link: "https://maps.google.com/?q=Addis+Ababa,+Ethiopia" },
];

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) return setErrors(newErrors);

    setIsSubmitting(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500)); 
    setSubmitSuccess(true);
    setIsSubmitting(false);
    setFormData({ name: "", email: "", subject: "", message: "" });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const inputBaseClass = "w-full px-4 py-3.5 rounded-xl border focus:outline-none focus:ring-1 focus:ring-emerald-500 transition-all duration-200 bg-zinc-50 dark:bg-[#09090B] border-zinc-200 dark:border-white/10 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-600";

  return (
    <section id="contact" className="py-24 bg-[#FAFAFA] dark:bg-[#09090B] px-4 sm:px-6 relative overflow-hidden">
      
      {/* Subtle Ambient Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl h-[500px] bg-emerald-500/5 dark:bg-emerald-500/10 blur-[120px] pointer-events-none rounded-full" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center justify-center p-3 rounded-2xl bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-6">
            <MessageSquare className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white mb-6">
            Let's <span className="text-zinc-400 dark:text-zinc-500">Connect</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto text-zinc-600 dark:text-zinc-400 leading-relaxed">
            Whether you have a project to discuss, a team to scale, or just want to say hello, I'm currently open to new opportunities.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 max-w-6xl mx-auto">
          
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-4 flex flex-col gap-4">
            {CONTACT_INFO.map((info, index) => (
              <motion.a
                key={info.title}
                href={info.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="group flex items-center p-6 rounded-2xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 hover:border-emerald-500/50 dark:hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-zinc-100 dark:bg-zinc-800 group-hover:bg-emerald-50 dark:group-hover:bg-emerald-500/10 transition-colors mr-5 shrink-0">
                  <info.icon className="w-5 h-5 text-zinc-500 dark:text-zinc-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 mb-1">{info.title}</h3>
                  <p className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{info.content}</p>
                </div>
              </motion.a>
            ))}
          </div>

          {/* Right Column: The Form */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }} 
            whileInView={{ opacity: 1, x: 0 }} 
            viewport={{ once: true }} 
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-8 p-8 md:p-10 rounded-3xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-white/10 shadow-xl shadow-zinc-200/20 dark:shadow-none"
          >
            <AnimatePresence mode="wait">
              {submitSuccess ? (
                <motion.div 
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex flex-col items-center justify-center text-center h-full min-h-[400px]"
                >
                  <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-500/10 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle2 className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-display font-semibold text-zinc-900 dark:text-white mb-3">Message Received</h3>
                  <p className="text-zinc-600 dark:text-zinc-400 max-w-md mx-auto mb-8">
                    Thank you for reaching out. I'll review your message and get back to you as soon as possible.
                  </p>
                  <button 
                    onClick={() => setSubmitSuccess(false)} 
                    className="px-6 py-2.5 rounded-full text-sm font-medium text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/10 transition-colors"
                  >
                    Send another message
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Name</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} className={`${inputBaseClass} ${errors.name ? "border-red-400 dark:border-red-500/50 focus:ring-red-500" : ""}`} placeholder="Jane Doe" />
                      {errors.name && <p className="text-xs font-medium text-red-500 mt-1">{errors.name}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Email</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} className={`${inputBaseClass} ${errors.email ? "border-red-400 dark:border-red-500/50 focus:ring-red-500" : ""}`} placeholder="jane@company.com" />
                      {errors.email && <p className="text-xs font-medium text-red-500 mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Subject</label>
                    <input type="text" name="subject" value={formData.subject} onChange={handleChange} className={`${inputBaseClass} ${errors.subject ? "border-red-400 dark:border-red-500/50 focus:ring-red-500" : ""}`} placeholder="Freelance Project Inquiry" />
                    {errors.subject && <p className="text-xs font-medium text-red-500 mt-1">{errors.subject}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Message</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} rows="5" className={`${inputBaseClass} resize-y min-h-[120px] ${errors.message ? "border-red-400 dark:border-red-500/50 focus:ring-red-500" : ""}`} placeholder="Tell me about your project..."></textarea>
                    {errors.message && <p className="text-xs font-medium text-red-500 mt-1">{errors.message}</p>}
                  </div>
                  
                  <div className="pt-2">
                    <button 
                      type="submit" 
                      disabled={isSubmitting} 
                      className={`w-full flex items-center justify-center px-8 py-4 rounded-xl text-sm font-medium transition-all duration-300 ${
                        isSubmitting 
                          ? "bg-zinc-100 text-zinc-400 dark:bg-zinc-800 dark:text-zinc-500 cursor-not-allowed" 
                          : "bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white shadow-lg shadow-zinc-900/10 dark:shadow-white/5"
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default Contact;